// Customers.tsx — customer list and customer detail (7 tabs).
//
// MANAGEMENT ONLY. There is no create path here by design: the Setup Wizard is
// the sole creation path (POST /tenants/provision-wizard), because it is the only
// flow that runs collision classification and issues the admin invite. A second
// create form would be a second, weaker door into tenant provisioning.
//
// Every mutation on this screen goes through `pmutate`, which requires a reason
// at the type level, and every one is preceded by PReasonPrompt.

import React, { useCallback, useEffect, useState } from "react";
import { PT, PT_NUM, fmtNum } from "../../platformTheme";
import { pget, pmutate, perr } from "./api";
import {
  PCard, PSection, PBtn, PBadge, PBanner, PEmpty, PSpinner, PTable, PRow, PTabs,
  PModal, PReasonPrompt, PInput, PSelect, PStat, PGrid, PId, PSecretField, PCheck,
  th, td, tdNum, card, statusTone, fmtDate,
} from "./ui";

export interface Tenant {
  tenant_id: string;
  name: string;
  status: string;
  edition: string;
  created_at: string;
  user_count: string | number;
  config?: Record<string, unknown> | null;
}

interface PlatformUser {
  id: string; email: string; name: string | null; role: string;
  is_active: boolean; totp_enabled: boolean; must_change_password: boolean; created_at: string;
}

// ── List ─────────────────────────────────────────────────────────────────────

export const CustomersList: React.FC<{ onOpen: (t: Tenant) => void }> = ({ onOpen }) => {
  const [rows, setRows] = useState<Tenant[] | null>(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setErr("");
    const r = await pget("/tenants");
    if (!r.ok) { setErr(perr(r, "Could not load customers")); setRows([]); return; }
    setRows(r.data?.tenants || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = (rows || []).filter((t) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return t.name?.toLowerCase().includes(s) || t.tenant_id?.toLowerCase().includes(s) || t.edition?.toLowerCase().includes(s);
  });

  return (
    <PSection
      title="Customers"
      sub={rows ? `${rows.length} total` : undefined}
      right={<PBtn small onClick={load}>Refresh</PBtn>}
    >
      <PBanner>{err}</PBanner>
      <PCard style={{ padding: 0 }}>
        <div style={{ padding: 12, borderBottom: `1px solid ${PT.brdSubtle}` }}>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by name, edition, or id…"
            style={{
              width: "100%", boxSizing: "border-box", fontFamily: PT.fontUI, fontSize: 12.5,
              padding: "7px 9px", borderRadius: PT.radius, border: `1px solid ${PT.brd}`,
              background: PT.bgInset, color: PT.tx,
            }}
          />
        </div>
        {rows === null ? <PSpinner /> : filtered.length === 0 ? (
          <PEmpty>{rows.length === 0 ? "No customers yet — provision one from the Setup Wizard." : "No customer matches that filter."}</PEmpty>
        ) : (
          <PTable
            minWidth={760}
            head={<>
              <th style={th}>Customer</th>
              <th style={th}>Status</th>
              <th style={th}>Edition</th>
              <th style={{ ...th, textAlign: "right" }}>Users</th>
              <th style={th}>Created</th>
            </>}
          >
            {filtered.map((t) => (
              <PRow key={t.tenant_id} onClick={() => onOpen(t)}>
                <td style={td}>
                  <div style={{ fontWeight: 600 }}>{t.name}</div>
                  <PId id={t.tenant_id} />
                </td>
                <td style={td}><PBadge tone={statusTone(t.status)}>{t.status}</PBadge></td>
                <td style={td}>
                  <span style={{ ...PT_NUM, fontSize: 11.5, color: PT.txM }}>{t.edition}</span>
                </td>
                <td style={tdNum}>{fmtNum(t.user_count)}</td>
                <td style={{ ...td, color: PT.txM, fontSize: 11.5 }}>{fmtDate(t.created_at)}</td>
              </PRow>
            ))}
          </PTable>
        )}
      </PCard>
    </PSection>
  );
};

// ── Detail ───────────────────────────────────────────────────────────────────

const TABS = [
  { id: "health", label: "Health" },
  { id: "users", label: "Users" },
  { id: "onboarding", label: "Onboarding" },
  { id: "integrations", label: "Integrations" },
  { id: "ai", label: "AI & Spend" },
  { id: "security", label: "Security" },
  { id: "actions", label: "Tenant Actions" },
  { id: "audit", label: "Audit" },
];

export const CustomerDetail: React.FC<{ tenant: Tenant; onBack: () => void; onChanged: () => void }> = ({
  tenant, onBack, onChanged,
}) => {
  const [tab, setTab] = useState("health");
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 14 }}>
        <PBtn small onClick={onBack}>← Customers</PBtn>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{tenant.name}</div>
          <div style={{ fontSize: 11.5, color: PT.txD }}>
            {tenant.edition} · {fmtNum(tenant.user_count)} users · created {fmtDate(tenant.created_at)}
          </div>
        </div>
        <PBadge tone={statusTone(tenant.status)}>{tenant.status}</PBadge>
      </div>

      <PTabs tabs={TABS} active={tab} onChange={setTab} />

      {tab === "health" && <HealthTab tenant={tenant} />}
      {tab === "users" && <UsersTab tenant={tenant} />}
      {tab === "onboarding" && <OnboardingTab tenant={tenant} />}
      {tab === "integrations" && <IntegrationsTab tenant={tenant} />}
      {tab === "ai" && <AiTab tenant={tenant} />}
      {tab === "security" && <SecurityTab tenant={tenant} />}
      {tab === "actions" && <ActionsTab tenant={tenant} onChanged={onChanged} onBack={onBack} />}
      {tab === "audit" && <AuditTab tenant={tenant} />}
    </div>
  );
};

// ── Health ───────────────────────────────────────────────────────────────────

const HealthTab: React.FC<{ tenant: Tenant }> = ({ tenant }) => {
  const [h, setH] = useState<any>(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    (async () => {
      const r = await pget(`/tenants/${tenant.tenant_id}/health`);
      if (!r.ok) { setErr(perr(r, "Could not load health")); return; }
      setH(r.data);
    })();
  }, [tenant.tenant_id]);

  if (err) return <PBanner>{err}</PBanner>;
  if (!h) return <PSpinner />;

  // Render whatever computeTenantHealth returned, rather than hard-coding a tile
  // list that silently goes blank when the backend adds or renames a metric.
  const entries = Object.entries(h).filter(
    ([k, v]) => !["tenant_id", "name", "status"].includes(k) && (typeof v === "number" || typeof v === "string"),
  );

  return (
    <PGrid>
      {entries.map(([k, v]) => (
        <PStat key={k} label={k.replace(/_/g, " ")} value={typeof v === "number" ? fmtNum(v) : String(v)} />
      ))}
      {entries.length === 0 && <PEmpty>No health metrics reported for this customer.</PEmpty>}
    </PGrid>
  );
};

// ── Users ────────────────────────────────────────────────────────────────────

const UsersTab: React.FC<{ tenant: Tenant }> = ({ tenant }) => {
  const [users, setUsers] = useState<PlatformUser[] | null>(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [prompt, setPrompt] = useState<null | { title: string; detail: React.ReactNode; danger?: boolean; run: (reason: string) => Promise<void> }>(null);
  const [busy, setBusy] = useState(false);
  const [pErr, setPErr] = useState("");
  const [adding, setAdding] = useState(false);
  const [nu, setNu] = useState({ email: "", name: "", role: "requester" });

  const load = useCallback(async () => {
    const r = await pget(`/tenants/${tenant.tenant_id}/users`);
    if (!r.ok) { setErr(perr(r, "Could not load users")); setUsers([]); return; }
    setUsers(r.data?.users || []);
  }, [tenant.tenant_id]);
  useEffect(() => { load(); }, [load]);

  const run = async (reason: string) => {
    if (!prompt) return;
    setBusy(true); setPErr("");
    try { await prompt.run(reason); setPrompt(null); await load(); }
    catch (e: any) { setPErr(e?.message || "Action failed"); }
    finally { setBusy(false); }
  };

  const act = (u: PlatformUser, kind: "deactivate" | "reactivate" | "reset-invite" | "delete") => {
    const base = `/tenants/${tenant.tenant_id}/users/${u.id}`;
    const labels: Record<string, string> = {
      deactivate: "Deactivate user", reactivate: "Reactivate user",
      "reset-invite": "Re-issue set-password invite", delete: "Delete user",
    };
    setPErr("");
    setPrompt({
      title: labels[kind],
      danger: kind === "delete",
      detail: (
        <>
          <strong>{u.email}</strong> in <strong>{tenant.name}</strong>.
          {kind === "reset-invite" && (
            <div style={{ marginTop: 7, color: PT.txM }}>
              This sends a fresh set-password link. <strong>No password is generated or shown</strong> —
              the console never displays or transmits a password.
            </div>
          )}
          {kind === "delete" && (
            <div style={{ marginTop: 7, color: PT.err }}>
              The server refuses this for the last admin and for an owner-of-record.
            </div>
          )}
        </>
      ),
      run: async (reason) => {
        const r = kind === "delete"
          ? await pmutate("DELETE", base, reason)
          : await pmutate("POST", `${base}/${kind}`, reason);
        if (!r.ok) throw new Error(perr(r));
        setMsg(kind === "reset-invite" ? `Set-password invite re-issued to ${u.email}.` : `${labels[kind]} complete.`);
      },
    });
  };

  const addUser = async (reason: string) => {
    const r = await pmutate("POST", `/tenants/${tenant.tenant_id}/users`, reason, {
      email: nu.email.trim().toLowerCase(), name: nu.name.trim(), role: nu.role,
    });
    if (!r.ok) throw new Error(perr(r));
    setMsg(`Invited ${nu.email} as ${nu.role}.`);
    setNu({ email: "", name: "", role: "requester" });
    setAdding(false);
  };

  return (
    <>
      <PBanner>{err}</PBanner>
      <PBanner tone="ok">{msg}</PBanner>
      <PSection title="Users" right={<PBtn small v="primary" onClick={() => { setAdding(true); }}>+ Add user</PBtn>}>
        <PCard style={{ padding: 0 }}>
          {users === null ? <PSpinner /> : users.length === 0 ? <PEmpty>No users.</PEmpty> : (
            <PTable
              minWidth={800}
              head={<>
                <th style={th}>User</th><th style={th}>Role</th><th style={th}>2FA</th>
                <th style={th}>Status</th><th style={{ ...th, textAlign: "right" }}>Actions</th>
              </>}
            >
              {users.map((u) => (
                <PRow key={u.id}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{u.name || "—"}</div>
                    <div style={{ fontSize: 11.5, color: PT.txD }}>{u.email}</div>
                  </td>
                  <td style={td}><PBadge tone={u.role === "admin" ? "info" : "neutral"}>{u.role}</PBadge></td>
                  <td style={td}>{u.totp_enabled ? <PBadge tone="ok">On</PBadge> : <PBadge tone="warn">Off</PBadge>}</td>
                  <td style={td}>{u.is_active ? <PBadge tone="ok">Active</PBadge> : <PBadge tone="neutral">Inactive</PBadge>}</td>
                  <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                    {/* "Reset password" in the design maps to reset-invite. There is no
                        endpoint that sets or reveals a password, and there must not be. */}
                    <PBtn small onClick={() => act(u, "reset-invite")} title="Re-issues a set-password invite; no password is shown">
                      Reset password
                    </PBtn>{" "}
                    {u.is_active
                      ? <PBtn small onClick={() => act(u, "deactivate")}>Deactivate</PBtn>
                      : <PBtn small onClick={() => act(u, "reactivate")}>Reactivate</PBtn>}{" "}
                    <PBtn small v="danger" onClick={() => act(u, "delete")}>Delete</PBtn>
                  </td>
                </PRow>
              ))}
            </PTable>
          )}
        </PCard>
      </PSection>

      <PReasonPrompt
        open={!!prompt}
        title={prompt?.title || ""}
        detail={prompt?.detail}
        danger={prompt?.danger}
        busy={busy}
        error={pErr}
        onCancel={() => setPrompt(null)}
        onConfirm={run}
      />

      <PModal
        open={adding}
        onClose={() => setAdding(false)}
        title={`Add user to ${tenant.name}`}
        footer={<>
          <PBtn onClick={() => setAdding(false)}>Cancel</PBtn>
          <PBtn
            v="primary"
            disabled={!nu.email.trim()}
            onClick={() => {
              setAdding(false);
              setPErr("");
              setPrompt({
                title: "Add user",
                detail: <>Invite <strong>{nu.email}</strong> to <strong>{tenant.name}</strong> as <strong>{nu.role}</strong>.</>,
                run: addUser,
              });
            }}
          >Continue</PBtn>
        </>}
      >
        <PInput label="Email" value={nu.email} onChange={(v) => setNu({ ...nu, email: v })} autoFocus />
        <PInput label="Name" value={nu.name} onChange={(v) => setNu({ ...nu, name: v })} />
        <PSelect
          label="Role" value={nu.role} onChange={(v) => setNu({ ...nu, role: v })}
          options={[
            { value: "requester", label: "requester" },
            { value: "lawyer", label: "lawyer" },
            { value: "legalops", label: "legalops" },
            { value: "admin", label: "admin" },
          ]}
          hint="The user receives a set-password invite. No password is created here."
        />
      </PModal>
    </>
  );
};

// ── Onboarding ───────────────────────────────────────────────────────────────

const OnboardingTab: React.FC<{ tenant: Tenant }> = ({ tenant }) => {
  const [data, setData] = useState<any>(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    (async () => {
      const r = await pget(`/tenants/${tenant.tenant_id}/onboarding`);
      if (!r.ok) { setErr(perr(r, "Could not load the onboarding profile")); return; }
      setData(r.data || {});
    })();
  }, [tenant.tenant_id]);

  if (err) return <PBanner>{err}</PBanner>;
  if (!data) return <PSpinner />;

  const profile = data.onboarding || data.profile || data;
  const rows = Object.entries(profile).filter(([, v]) => v !== null && v !== undefined && typeof v !== "function");

  return (
    <PSection title="Recorded onboarding profile" sub="Captured at provisioning; read-only here">
      <PCard style={{ padding: 0 }}>
        {rows.length === 0 ? <PEmpty>No onboarding profile recorded for this customer.</PEmpty> : (
          <PTable minWidth={520} head={<><th style={th}>Field</th><th style={th}>Value</th></>}>
            {rows.map(([k, v]) => (
              <PRow key={k}>
                <td style={{ ...td, color: PT.txM, width: 240 }}>{k.replace(/_/g, " ")}</td>
                <td style={{ ...td, fontFamily: PT.fontMono, fontSize: 11.5 }}>
                  {typeof v === "object" ? JSON.stringify(v) : String(v)}
                </td>
              </PRow>
            ))}
          </PTable>
        )}
      </PCard>
    </PSection>
  );
};

// ── Security: password policy ────────────────────────────────────────────────
//
// THE FOUR VALUES WERE ALWAYS ENFORCED AND NEVER MANAGEABLE FROM HERE.
// utils/passwordPolicy.js loads them from app_settings and validatePassword
// applies them on every password mutation. The tenant-side editor
// (routes/admin.js) runs in the CALLER'S tenant, so an operator could only ever
// have edited the platform tenant's own policy — never a customer's. Settings
// said "security policy … has no operator-level endpoint today" rather than
// shipping a card that could not save. Now there is one, per customer.
//
// This screen is deliberately plain about scope: it sets a floor for NEW
// passwords. It does not expire anything, cannot read a password, and does not
// touch sessions. A value that is not stored is shown as the built-in default
// rather than as a choice somebody made.

interface PasswordPolicy {
  min_length: number;
  require_upper: boolean;
  require_digit: boolean;
  require_special: boolean;
  stored: Record<string, boolean>;
}

const SecurityTab: React.FC<{ tenant: Tenant }> = ({ tenant }) => {
  const [policy, setPolicy] = useState<PasswordPolicy | null>(null);
  const [minLength, setMinLength] = useState("8");
  const [upper, setUpper] = useState(false);
  const [digit, setDigit] = useState(false);
  const [special, setSpecial] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [prompt, setPrompt] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pErr, setPErr] = useState("");

  const load = useCallback(async () => {
    setErr("");
    const r = await pget(`/tenants/${tenant.tenant_id}/password-policy`);
    if (!r.ok) { setErr(perr(r, "Could not load the password policy")); return; }
    const p: PasswordPolicy = r.data?.policy;
    setPolicy(p);
    setMinLength(String(p.min_length));
    setUpper(p.require_upper); setDigit(p.require_digit); setSpecial(p.require_special);
  }, [tenant.tenant_id]);
  useEffect(() => { load(); }, [load]);

  if (err) return <PBanner>{err}</PBanner>;
  if (!policy) return <PSpinner />;

  const n = parseInt(minLength, 10);
  const lengthValid = Number.isFinite(n) && n >= 4 && n <= 128;
  const weakening =
    (lengthValid && n < policy.min_length) ||
    (policy.require_upper && !upper) ||
    (policy.require_digit && !digit) ||
    (policy.require_special && !special);
  const dirty =
    !lengthValid ? true
    : n !== policy.min_length || upper !== policy.require_upper
      || digit !== policy.require_digit || special !== policy.require_special;
  const anyStored = Object.values(policy.stored || {}).some(Boolean);

  return (
    <>
      <PBanner tone="ok">{msg}</PBanner>
      <PSection title="Password policy" sub={`applies to new passwords for ${tenant.name}`}>
        <PBanner tone="info">
          This sets the rules a <strong>new</strong> password must satisfy. It does not expire or
          reset anyone's existing password, does not sign anyone out, and no password is readable
          from here. Changes take effect immediately for this customer, though another API instance
          may apply the previous rules for up to a minute.
        </PBanner>

        <PCard>
          {!anyStored && (
            <div style={{ fontSize: 12, color: PT.txM, marginBottom: 12 }}>
              Nothing is stored for this customer yet — the values below are the built-in defaults,
              not a choice anyone made. Saving records them explicitly.
            </div>
          )}

          <PInput
            label="Minimum length"
            value={minLength}
            onChange={setMinLength}
            hint="Between 4 and 128."
          />
          {!lengthValid && (
            <div style={{ fontSize: 12, color: PT.errTx, marginTop: -6, marginBottom: 10 }}>
              Enter a whole number between 4 and 128.
            </div>
          )}

          <PCheck checked={upper} onChange={setUpper} label="Require an uppercase letter" />
          <PCheck checked={digit} onChange={setDigit} label="Require a digit" />
          <PCheck checked={special} onChange={setSpecial} label="Require a special character" />

          {weakening && (
            <div style={{ fontSize: 12, color: PT.warnTx, marginTop: 10 }}>
              This is <strong>weaker</strong> than the current policy. That is allowed — a customer
              migrating from a system with looser rules is a real case — and the change is recorded
              in the operator audit with its direction.
            </div>
          )}

          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <PBtn v="primary" disabled={!dirty || !lengthValid} onClick={() => setPrompt(true)}>Save</PBtn>
            <PBtn disabled={!dirty} onClick={load}>Discard</PBtn>
          </div>
        </PCard>
      </PSection>

      <PReasonPrompt
        open={prompt}
        title="Change the password policy"
        danger={weakening}
        detail={
          <>
            Setting <strong>{tenant.name}</strong>'s password rules to: at least {n} characters
            {[upper ? "an uppercase letter" : null, digit ? "a digit" : null, special ? "a special character" : null]
              .filter(Boolean).length > 0
              ? <>, including {[upper ? "an uppercase letter" : null, digit ? "a digit" : null, special ? "a special character" : null].filter(Boolean).join(", ")}</>
              : ", with no character-type requirements"}.
            <div style={{ marginTop: 7, color: PT.txM }}>
              Existing passwords are unchanged. Nobody is signed out.
            </div>
          </>
        }
        busy={busy}
        error={pErr}
        onCancel={() => setPrompt(false)}
        onConfirm={async (reason) => {
          setBusy(true); setPErr("");
          const r = await pmutate("PUT", `/tenants/${tenant.tenant_id}/password-policy`, reason, {
            min_length: n, require_upper: upper, require_digit: digit, require_special: special,
          });
          setBusy(false);
          if (!r.ok) { setPErr(perr(r)); return; }
          setPrompt(false);
          setMsg(`Password policy saved for ${tenant.name}. ${r.data?.note || ""}`);
          load();
        }}
      />
    </>
  );
};

// ── Integrations (credential staging) ────────────────────────────────────────

/**
 * One credential field as the backend describes it
 * (utils/operatorCredentials.js viewCredentials).
 */
interface CredField {
  label: string;
  secret: boolean;
  required: boolean;
  configured: boolean;
  value: string | null;   // null for every secret field, always
}

const IntegrationsTab: React.FC<{ tenant: Tenant }> = ({ tenant }) => {
  const [types, setTypes] = useState<any[] | null>(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [staging, setStaging] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, CredField>>({});
  const [loadingFields, setLoadingFields] = useState(false);
  const [input, setInput] = useState<Record<string, string>>({});
  const [prompt, setPrompt] = useState(false);
  const [mode, setMode] = useState<"stage" | "clear">("stage");
  const [busy, setBusy] = useState(false);
  const [pErr, setPErr] = useState("");
  // Which types are already configured, read per tenant rather than guessed.
  const [configured, setConfigured] = useState<Record<string, boolean>>({});

  // ── 2026-07-28. THIS PANEL COULD NOT STAGE A CREDENTIAL AT ALL. ────────────
  //
  // Reported as "Graph, Harvey and DocuSign are missing required fields". The
  // fields were never missing from the backend — utils/operatorCredentials.js
  // has carried the full schema for all three since it was written (Graph: six
  // fields; Harvey: the BYO key; DocuSign: seven). Three separate bugs in this
  // component meant none of them reached the screen and nothing could be saved:
  //
  //   1. `setFields(r.data)` stored the WHOLE response. The endpoint returns
  //      { type, label, configured, fields: {...} }, so the modal below iterated
  //      the envelope and rendered inputs labelled "label", "configured" and
  //      "fields" — and not one real credential field. That is the "missing
  //      required fields" the operator saw.
  //   2. `pmutate(..., input)` sent the values at the TOP LEVEL of the body. The
  //      route reads `req.body.values` (platformAdmin.js), so `values` arrived
  //      undefined, stageCredentials threw no_credential_values, and staging
  //      answered 400 for every type. Unreachable behind (1), which is the only
  //      reason it was never reported as a save failure.
  //   3. The card's Configured badge read `t.configured` off GET
  //      /credential-types, which returns only { type, label }. Every type read
  //      "Not set" however much was staged.
  //
  // Secrets remain write-only end to end: the endpoint returns `value: null` for
  // every secret field, and PSecretField has no prop through which a value could
  // be passed in. The secret/non-secret split now comes from the schema the
  // backend sends rather than from a regex over the field name — a name-shaped
  // guess would have classified `docusign_account_id` correctly by luck and
  // something else incorrectly by the same luck.
  const loadTypes = useCallback(async () => {
    const r = await pget("/credential-types");
    if (!r.ok) { setErr(perr(r, "Could not load credential types")); setTypes([]); return; }
    const list = r.data?.types || r.data?.credential_types || [];
    const arr: any[] = Array.isArray(list) ? list : Object.keys(list).map((k) => ({ type: k, ...list[k] }));
    setTypes(arr);
    // Configured state is per TENANT and only the per-type read knows it.
    const marks: Record<string, boolean> = {};
    await Promise.all(arr.map(async (t) => {
      const d = await pget(`/tenants/${tenant.tenant_id}/credentials/${t.type}`);
      if (d.ok) marks[t.type] = !!d.data?.configured;
    }));
    setConfigured(marks);
  }, [tenant.tenant_id]);

  useEffect(() => { loadTypes(); }, [loadTypes]);

  const open = async (type: string) => {
    setStaging(type); setInput({}); setPErr(""); setFields({}); setLoadingFields(true);
    const r = await pget(`/tenants/${tenant.tenant_id}/credentials/${type}`);
    setLoadingFields(false);
    // `.fields`, not the envelope.
    setFields(r.ok ? ((r.data?.fields || {}) as Record<string, CredField>) : {});
    if (!r.ok) setPErr(perr(r, "Could not load this credential type"));
  };

  const save = async (reason: string) => {
    if (!staging) return;
    const values = Object.fromEntries(
      Object.entries(input).map(([k, v]) => [k, v.trim()]).filter(([, v]) => v !== "")
    );
    if (Object.keys(values).length === 0) { setPErr("Enter at least one field to stage."); return; }
    setBusy(true); setPErr("");
    // `values`, which is what the route reads. Only fields the operator typed
    // are sent — an untouched field must not blank a stored one.
    const r = await pmutate("PUT", `/tenants/${tenant.tenant_id}/credentials/${staging}`, reason, { values });
    setBusy(false);
    if (!r.ok) { setPErr(perr(r)); return; }
    const label = types?.find((t) => t.type === staging)?.label || staging;
    setPrompt(false); setStaging(null); setInput({});
    setMsg(`${label} credentials staged for ${tenant.name} (encrypted at rest).`);
    loadTypes();
  };

  const clear = async (reason: string) => {
    if (!staging) return;
    setBusy(true); setPErr("");
    const r = await pmutate("DELETE", `/tenants/${tenant.tenant_id}/credentials/${staging}`, reason);
    setBusy(false);
    if (!r.ok) { setPErr(perr(r)); return; }
    const label = types?.find((t) => t.type === staging)?.label || staging;
    setPrompt(false); setStaging(null); setInput({});
    setMsg(`${label} credentials cleared for ${tenant.name}.`);
    loadTypes();
  };

  const typeName = (t: any) => t.type || t.name || t.key || String(t);
  const staged = (t: any) => !!configured[typeName(t)];
  const missingRequired = Object.entries(fields)
    .filter(([k, f]) => f.required && !f.configured && !(input[k] || "").trim())
    .map(([, f]) => f.label);

  return (
    <>
      <PBanner>{err}</PBanner>
      <PBanner tone="ok">{msg}</PBanner>
      <PSection title="Operator credential staging" sub="Encrypted at rest · masked on view · reason-required">
        {types === null ? <PSpinner /> : types.length === 0 ? (
          <PCard><PEmpty>No credential types are available.</PEmpty></PCard>
        ) : (
          <PGrid min={220}>
            {types.map((t) => {
              const name = typeName(t);
              return (
                <PCard key={name}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 9 }}>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{t.label || name}</span>
                    <div style={{ flex: 1 }} />
                    {staged(t) ? <PBadge tone="ok">Configured</PBadge> : <PBadge tone="neutral">Not set</PBadge>}
                  </div>
                  <PBtn small onClick={() => open(name)}>Stage credentials</PBtn>
                </PCard>
              );
            })}
          </PGrid>
        )}
      </PSection>

      <PModal
        open={!!staging}
        onClose={() => setStaging(null)}
        title={`Stage credentials — ${types?.find((t) => t.type === staging)?.label || staging}`}
        footer={<>
          <PBtn onClick={() => setStaging(null)}>Cancel</PBtn>
          {staging && configured[staging] && (
            <PBtn v="danger" onClick={() => { setMode("clear"); setPrompt(true); }}>Clear all</PBtn>
          )}
          <PBtn
            v="primary"
            disabled={Object.values(input).every((v) => !v.trim())}
            onClick={() => { setMode("stage"); setPrompt(true); }}
          >Continue</PBtn>
        </>}
      >
        {loadingFields ? <PSpinner /> : Object.keys(fields).length === 0 ? (
          <div style={{ fontSize: 12.5, color: PT.err, marginBottom: 12 }}>
            The server returned no field list for this credential type. Nothing is entered blind —
            staging is not offered.
          </div>
        ) : (
          <>
            <div style={{ fontSize: 12, color: PT.txM, marginBottom: 12, lineHeight: 1.5 }}>
              Only the fields you type are written; the rest keep whatever is stored. Secrets are
              encrypted at rest and are never returned — a configured secret shows as configured,
              never as a value.
            </div>
            {missingRequired.length > 0 && (
              <div style={{ fontSize: 12, color: PT.warnTx, marginBottom: 12 }}>
                Still required: {missingRequired.join(", ")}
              </div>
            )}
            {Object.entries(fields).map(([k, f]) => (
              f.secret ? (
                <PSecretField
                  key={k}
                  label={f.label + (f.required ? " *" : "")}
                  configured={f.configured}
                  value={input[k] || ""}
                  onChange={(val) => setInput({ ...input, [k]: val })}
                  hint={f.configured ? "Stored. Enter a value to replace it." : undefined}
                />
              ) : (
                <PInput
                  key={k}
                  label={f.label + (f.required ? " *" : "")}
                  value={input[k] ?? (f.value ?? "")}
                  onChange={(val) => setInput({ ...input, [k]: val })}
                />
              )
            ))}
          </>
        )}
      </PModal>

      <PReasonPrompt
        open={prompt}
        title={mode === "clear" ? "Clear credentials" : "Stage credentials"}
        detail={mode === "clear"
          ? <>Removing every stored credential for <strong>{types?.find((t) => t.type === staging)?.label || staging}</strong> on <strong>{tenant.name}</strong>. The customer falls back to the KontractAI default where one exists.</>
          : <>Writing credentials for <strong>{types?.find((t) => t.type === staging)?.label || staging}</strong> on <strong>{tenant.name}</strong>. Secrets are encrypted at rest and never returned.</>}
        danger={mode === "clear"}
        busy={busy}
        error={pErr}
        onCancel={() => setPrompt(false)}
        onConfirm={mode === "clear" ? clear : save}
      />
    </>
  );
};

// ── AI & Spend ───────────────────────────────────────────────────────────────

const PROVIDERS = ["anthropic", "openai", "groq"] as const;

// The platform/bootstrap tenant is not a customer: it has no BYO key surface and
// no per-tenant routing overrides, and the backend refuses both reads with
// `400 cannot_modify_platform_tenant`. That is a mutation-shaped error on a READ,
// and it rendered as a red failure banner on a tab the operator merely opened.
// The condition is real; the presentation was wrong. We do not call those
// endpoints for the platform tenant at all — which also avoids a pointless
// cross-tenant read — and state the fact instead.
const PLATFORM_TENANT_ID = "00000000-0000-0000-0000-000000000001";

// ── AI spend for ONE customer ────────────────────────────────────────────────
//
// The tab is called "AI & Spend" and showed no spend. For the platform tenant it
// showed "Not applicable" and stopped there — so the one customer whose spend
// the operator most wanted, KontractAI Bootstrap, was the one customer with no
// figure at all. Bootstrap is a real tenant with real usage; "it has no BYO key"
// is true and is a different statement from "it has no spend".
//
// Reads GET /usage/spend?tenant_id=… , the same endpoint Usage & Spend uses, so
// the two screens cannot disagree. It depends on the cross-tenant read in this
// same branch: before that fix this panel would have returned the OPERATOR's
// rows for every customer, which is worse than showing nothing.
const TenantSpend: React.FC<{ tenant: Tenant; days?: number }> = ({ tenant, days = 30 }) => {
  const [rows, setRows] = useState<any[] | null>(null);
  const [totals, setTotals] = useState<{ calls: number; errors: number; est_cost: number } | null>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setErr(""); setBusy(true);
    const r = await pget("/usage/spend", { days, tenant_id: tenant.tenant_id });
    setBusy(false);
    if (!r.ok) { setErr(perr(r, "Could not load this customer's AI spend")); setRows([]); return; }
    setRows(r.data?.rows || []);
    setTotals(r.data?.totals || null);
  }, [tenant.tenant_id, days]);
  useEffect(() => { load(); }, [load]);

  const cost = Number(totals?.est_cost || 0);
  const calls = Number(totals?.calls || 0);

  return (
    <PSection
      title="AI spend"
      sub={`${days}-day window · from the daily rollup`}
      right={<PBtn small disabled={busy} onClick={load}>{busy ? "Refreshing…" : "Refresh"}</PBtn>}
    >
      <PBanner>{err}</PBanner>
      <PGrid min={175}>
        <PStat
          label={`Spend · ${days}d`}
          value={rows === null ? "…" : `$${cost.toFixed(2)}`}
          // $0.00 with traffic behind it is a PRICING gap, not a spend of
          // nothing: est_cost is 0 for any model with no ai_model_prices row.
          // Saying which of the three states this is keeps a zero from reading
          // as a fact about how much this customer costs.
          sub={
            rows === null ? "loading"
            : calls === 0 ? "no calls recorded"
            : cost === 0 ? `${fmtNum(calls)} calls · no prices set for these models`
            : `${fmtNum(calls)} calls`
          }
        />
        <PStat label="Calls" value={rows === null ? "…" : fmtNum(calls)} sub="routed model calls" />
        <PStat
          label="Errors" value={rows === null ? "…" : fmtNum(totals?.errors)}
          sub={calls ? `${((Number(totals?.errors || 0) / calls) * 100).toFixed(1)}% of calls` : "—"}
        />
      </PGrid>

      <div style={{ marginTop: 12 }}>
        <PCard style={{ padding: 0 }}>
          {rows === null ? <PSpinner /> : rows.length === 0 ? (
            <PEmpty>
              No rollup rows for this customer in the window. The rollup aggregates yesterday and
              earlier, so usage from today appears tomorrow.
            </PEmpty>
          ) : (
            <PTable
              minWidth={620}
              head={<>
                <th style={th}>Provider</th><th style={th}>Model</th>
                <th style={{ ...th, textAlign: "right" }}>Calls</th>
                <th style={{ ...th, textAlign: "right" }}>In</th>
                <th style={{ ...th, textAlign: "right" }}>Out</th>
                <th style={{ ...th, textAlign: "right" }}>Est. cost</th>
              </>}
            >
              {rows.map((r, i) => (
                <PRow key={i}>
                  <td style={td}>{r.provider_display_name || r.provider}</td>
                  <td style={{ ...td, fontFamily: PT.fontMono, fontSize: 11.5 }}>{r.model}</td>
                  <td style={tdNum}>{fmtNum(r.calls)}</td>
                  <td style={{ ...tdNum, color: PT.txM }}>{fmtNum(r.input_tokens)}</td>
                  <td style={{ ...tdNum, color: PT.txM }}>{fmtNum(r.output_tokens)}</td>
                  <td style={{ ...tdNum, fontWeight: 600 }}>${Number(r.est_cost || 0).toFixed(2)}</td>
                </PRow>
              ))}
            </PTable>
          )}
        </PCard>
      </div>
    </PSection>
  );
};

const AiTab: React.FC<{ tenant: Tenant }> = ({ tenant }) => {
  const [keys, setKeys] = useState<Record<string, boolean>>({});
  const [entry, setEntry] = useState<Record<string, string>>({});
  const [routing, setRouting] = useState<any[] | null>(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [prompt, setPrompt] = useState<null | { title: string; detail: React.ReactNode; danger?: boolean; run: (r: string) => Promise<void> }>(null);
  const [busy, setBusy] = useState(false);
  const [pErr, setPErr] = useState("");

  const isPlatform = String(tenant.tenant_id) === PLATFORM_TENANT_ID;

  const load = useCallback(async () => {
    if (isPlatform) { setRouting([]); return; }
    const k = await pget(`/tenants/${tenant.tenant_id}/ai-key`);
    if (k.ok) {
      const d = k.data || {};
      const map: Record<string, boolean> = {};
      for (const p of PROVIDERS) map[p] = !!(d[p]?.configured ?? d[p] ?? d.providers?.[p]);
      setKeys(map);
    }
    const r = await pget(`/tenants/${tenant.tenant_id}/ai-routing`);
    if (!r.ok) { setErr(perr(r, "Could not load AI routing")); setRouting([]); return; }
    setRouting(r.data?.tiers || r.data?.routing || []);
  }, [tenant.tenant_id, isPlatform]);
  useEffect(() => { load(); }, [load]);

  const run = async (reason: string) => {
    if (!prompt) return;
    setBusy(true); setPErr("");
    try { await prompt.run(reason); setPrompt(null); await load(); }
    catch (e: any) { setPErr(e?.message || "Action failed"); }
    finally { setBusy(false); }
  };

  if (isPlatform) {
    return (
      <>
        <PCard style={{ borderColor: PT.brdStrong, background: PT.bgInset }}>
          <PBadge tone="neutral">No BYO key or routing override</PBadge>
          <div style={{ marginTop: 11, fontSize: 13, color: PT.tx, lineHeight: 1.6, maxWidth: 720 }}>
            This is the <strong>platform tenant</strong>, not a customer. It has no BYO AI key and no
            per-tenant routing overrides — the platform's own keys and routing catalog live under{" "}
            <strong>Settings ▸ Global AI</strong>.
          </div>
        </PCard>
        {/* But it DOES have spend. The badge used to read "Not applicable" and
            the tab stopped here, so the one tenant whose usage the operator most
            wanted to see was the only one with no figure at all. */}
        <TenantSpend tenant={tenant} />
      </>
    );
  }

  return (
    <>
      <PBanner>{err}</PBanner>
      <PBanner tone="ok">{msg}</PBanner>

      <TenantSpend tenant={tenant} />

      <PSection title="BYO AI keys" sub="Per-tenant · validated on save · write-only">
        <PGrid min={260}>
          {PROVIDERS.map((p) => (
            <PCard key={p}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <span style={{ fontWeight: 600, fontSize: 13, textTransform: "capitalize" }}>{p}</span>
                <div style={{ flex: 1 }} />
                {keys[p] ? <PBadge tone="ok">Configured</PBadge> : <PBadge tone="neutral">Platform default</PBadge>}
              </div>
              <PSecretField
                label="API key"
                configured={!!keys[p]}
                value={entry[p] || ""}
                onChange={(v) => setEntry({ ...entry, [p]: v })}
                hint="Validated against the provider on save. Never returned by the API."
              />
              <div style={{ display: "flex", gap: 7 }}>
                <PBtn
                  small v="primary"
                  disabled={!(entry[p] || "").trim()}
                  onClick={() => setPrompt({
                    title: `Set ${p} key`,
                    detail: <>Setting the <strong>{p}</strong> BYO key for <strong>{tenant.name}</strong>.</>,
                    run: async (reason) => {
                      const r = await pmutate("PUT", `/tenants/${tenant.tenant_id}/ai-key`, reason, { provider: p, api_key: entry[p] });
                      if (!r.ok) throw new Error(perr(r));
                      setEntry({ ...entry, [p]: "" });
                      setMsg(`${p} key saved.`);
                    },
                  })}
                >Save</PBtn>
                {keys[p] && (
                  <PBtn
                    small v="danger"
                    onClick={() => setPrompt({
                      title: `Remove ${p} key`,
                      danger: true,
                      detail: <>This customer will fall back to the platform default key for <strong>{p}</strong>.</>,
                      run: async (reason) => {
                        const r = await pmutate("DELETE", `/tenants/${tenant.tenant_id}/ai-key`, reason, { provider: p });
                        if (!r.ok) throw new Error(perr(r));
                        setMsg(`${p} key removed.`);
                      },
                    })}
                  >Remove</PBtn>
                )}
              </div>
            </PCard>
          ))}
        </PGrid>
      </PSection>

      <PSection title="AI routing temperature overrides" sub="Per-tier, per-customer. Reasoning models omit temperature.">
        <PCard style={{ padding: 0 }}>
          {routing === null ? <PSpinner /> : routing.length === 0 ? (
            <PEmpty>No tier overrides are configured — this customer follows the global routing catalog.</PEmpty>
          ) : (
            <PTable
              minWidth={640}
              head={<><th style={th}>Tier</th><th style={th}>Model</th><th style={{ ...th, textAlign: "right" }}>Override</th><th style={th} /></>}
            >
              {routing.map((t: any) => (
                <PRow key={t.tier}>
                  <td style={td}>{t.tier}</td>
                  <td style={{ ...td, fontFamily: PT.fontMono, fontSize: 11.5 }}>{t.model || t.model_id || "—"}</td>
                  <td style={tdNum}>{t.temperature ?? "—"}</td>
                  <td style={{ ...td, textAlign: "right" }}>
                    {t.temperature != null && (
                      <PBtn
                        small v="danger"
                        onClick={() => setPrompt({
                          title: `Clear override for ${t.tier}`,
                          danger: true,
                          detail: <>This tier will follow the global temperature again.</>,
                          run: async (reason) => {
                            const r = await pmutate("DELETE", `/tenants/${tenant.tenant_id}/ai-routing/${t.tier}`, reason);
                            if (!r.ok) throw new Error(perr(r));
                            setMsg(`Override cleared for ${t.tier}.`);
                          },
                        })}
                      >Clear</PBtn>
                    )}
                  </td>
                </PRow>
              ))}
            </PTable>
          )}
        </PCard>
      </PSection>

      <PReasonPrompt
        open={!!prompt}
        title={prompt?.title || ""}
        detail={prompt?.detail}
        danger={prompt?.danger}
        busy={busy}
        error={pErr}
        onCancel={() => setPrompt(null)}
        onConfirm={run}
      />
    </>
  );
};

// ── Tenant Actions ───────────────────────────────────────────────────────────

const ActionsTab: React.FC<{ tenant: Tenant; onChanged: () => void; onBack: () => void }> = ({
  tenant, onChanged, onBack,
}) => {
  const [prompt, setPrompt] = useState<null | {
    title: string; detail: React.ReactNode; danger?: boolean; confirmName?: string; run: (r: string) => Promise<void>;
  }>(null);
  const [busy, setBusy] = useState(false);
  const [pErr, setPErr] = useState("");
  const [msg, setMsg] = useState("");

  const run = async (reason: string) => {
    if (!prompt) return;
    setBusy(true); setPErr("");
    try { await prompt.run(reason); setPrompt(null); onChanged(); }
    catch (e: any) { setPErr(e?.message || "Action failed"); }
    finally { setBusy(false); }
  };

  const simple = (path: string, title: string, detail: React.ReactNode, danger?: boolean) => () =>
    setPrompt({
      title, detail, danger,
      run: async (reason) => {
        const r = await pmutate("POST", `/tenants/${tenant.tenant_id}/${path}`, reason);
        if (!r.ok) throw new Error(perr(r));
        setMsg(`${title} complete.`);
      },
    });

  const ACTIONS: { title: string; body: string; btn: string; danger?: boolean; onClick: () => void }[] = [
    {
      title: "Suspend", btn: "Suspend", body: "Sign-in is blocked for every user. Data is retained and the customer can be reactivated.",
      onClick: simple("suspend", "Suspend customer", <>Users of <strong>{tenant.name}</strong> will be unable to sign in.</>),
    },
    {
      title: "Reactivate", btn: "Reactivate", body: "Restores sign-in for a suspended customer.",
      onClick: simple("reactivate", "Reactivate customer", <>Restore access for <strong>{tenant.name}</strong>.</>),
    },
    {
      title: "Offboard", btn: "Offboard", body: "Ends the engagement. Data is retained; the customer becomes read-only and can be re-onboarded.",
      danger: true,
      onClick: simple("offboard", "Offboard customer", <>Offboard <strong>{tenant.name}</strong>. Data is retained.</>, true),
    },
    {
      title: "Resend admin invite", btn: "Resend invite", body: "Re-issues the set-password link to the customer's admin. No password is shown.",
      onClick: simple("resend-invite", "Resend admin invite", <>Re-issue the set-password link for <strong>{tenant.name}</strong>'s admin.</>),
    },
  ];

  return (
    <>
      <PBanner tone="ok">{msg}</PBanner>
      <PGrid min={280}>
        {ACTIONS.map((a) => (
          <PCard key={a.title}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{a.title}</div>
            <div style={{ fontSize: 12, color: PT.txM, marginBottom: 11, lineHeight: 1.5 }}>{a.body}</div>
            <PBtn small v={a.danger ? "danger" : "default"} onClick={a.onClick}>{a.btn}</PBtn>
          </PCard>
        ))}

        <PCard style={{ borderColor: PT.err }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, color: PT.err }}>Delete permanently</div>
          <div style={{ fontSize: 12, color: PT.txM, marginBottom: 11, lineHeight: 1.5 }}>
            Irreversible. Removes the customer and its data. The server additionally requires
            the organisation name to be typed exactly.
          </div>
          <PBtn
            small v="danger"
            onClick={() => setPrompt({
              title: "Delete customer permanently",
              danger: true,
              confirmName: tenant.name,
              detail: (
                <>
                  This <strong>cannot be undone</strong>. <strong>{tenant.name}</strong> and its data will be removed.
                </>
              ),
              run: async (reason) => {
                const r = await pmutate("POST", `/tenants/${tenant.tenant_id}/delete-permanently`, reason, { confirm_name: tenant.name });
                if (!r.ok) throw new Error(perr(r));
                onBack();
              },
            })}
          >Delete permanently</PBtn>
        </PCard>
      </PGrid>

      <PReasonPrompt
        open={!!prompt}
        title={prompt?.title || ""}
        detail={prompt?.detail}
        danger={prompt?.danger}
        confirmName={prompt?.confirmName}
        busy={busy}
        error={pErr}
        onCancel={() => setPrompt(null)}
        onConfirm={run}
      />
    </>
  );
};

// ── Per-customer audit ───────────────────────────────────────────────────────

const AuditTab: React.FC<{ tenant: Tenant }> = ({ tenant }) => {
  const [rows, setRows] = useState<any[] | null>(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    (async () => {
      const r = await pget("/audit", { tenant_id: tenant.tenant_id, limit: 200 });
      if (!r.ok) { setErr(perr(r, "Could not load audit entries")); setRows([]); return; }
      setRows(r.data?.entries || []);
    })();
  }, [tenant.tenant_id]);

  if (err) return <PBanner>{err}</PBanner>;
  return (
    <PCard style={{ padding: 0 }}>
      {rows === null ? <PSpinner /> : rows.length === 0 ? (
        <PEmpty>No operator actions recorded against this customer.</PEmpty>
      ) : (
        <PTable
          minWidth={720}
          head={<><th style={th}>Time</th><th style={th}>Action</th><th style={th}>Reason</th><th style={th}>Actor</th></>}
        >
          {rows.map((e) => (
            <PRow key={e.id}>
              <td style={{ ...td, whiteSpace: "nowrap", color: PT.txM, fontSize: 11.5 }}>{fmtDate(e.occurred_at)}</td>
              <td style={td}><PBadge tone="info">{e.action}</PBadge></td>
              <td style={{ ...td, color: PT.txM }}>{e.reason || <span style={{ color: PT.txD }}>—</span>}</td>
              <td style={td}><PId id={e.actor_user_id} /></td>
            </PRow>
          ))}
        </PTable>
      )}
    </PCard>
  );
};
